import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Bus, ArrowLeft, Trophy, Star, Volume2,
  RotateCcw, Sparkles, Heart, Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BlendingBusGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [mistakes, setMistakes] = useState(0);
  const [currentWord, setCurrentWord] = useState(null);
  const [revealedLetters, setRevealedLetters] = useState([]);
  const [options, setOptions] = useState([]);
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [busPosition, setBusPosition] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // 1 minute timer

  // CVC words by level (Consonant-Vowel-Consonant)
  const wordLists = {
    1: [
      { word: 'cat', sounds: ['c', 'a', 't'], image: '🐱' },
      { word: 'dog', sounds: ['d', 'o', 'g'], image: '🐕' },
      { word: 'sun', sounds: ['s', 'u', 'n'], image: '☀️' },
      { word: 'hat', sounds: ['h', 'a', 't'], image: '🎩' },
      { word: 'pen', sounds: ['p', 'e', 'n'], image: '🖊️' },
      { word: 'cup', sounds: ['c', 'u', 'p'], image: '☕' },
      { word: 'bed', sounds: ['b', 'e', 'd'], image: '🛏️' },
      { word: 'map', sounds: ['m', 'a', 'p'], image: '🗺️' },
    ],
    2: [
      { word: 'frog', sounds: ['f', 'r', 'o', 'g'], image: '🐸' },
      { word: 'swim', sounds: ['s', 'w', 'i', 'm'], image: '🏊' },
      { word: 'jump', sounds: ['j', 'u', 'm', 'p'], image: '🦘' },
      { word: 'snail', sounds: ['s', 'n', 'ai', 'l'], image: '🐌' },
      { word: 'tree', sounds: ['t', 'r', 'ee'], image: '🌳' },
      { word: 'boat', sounds: ['b', 'oa', 't'], image: '⛵' },
      { word: 'rain', sounds: ['r', 'ai', 'n'], image: '🌧️' },
      { word: 'moon', sounds: ['m', 'oo', 'n'], image: '🌙' },
    ],
    3: [
      { word: 'chair', sounds: ['ch', 'air'], image: '🪑' },
      { word: 'sheep', sounds: ['sh', 'ee', 'p'], image: '🐑' },
      { word: 'think', sounds: ['th', 'i', 'nk'], image: '🤔' },
      { word: 'phone', sounds: ['ph', 'o', 'ne'], image: '📱' },
      { word: 'queen', sounds: ['qu', 'ee', 'n'], image: '👑' },
      { word: 'night', sounds: ['n', 'igh', 't'], image: '🌙' },
      { word: 'light', sounds: ['l', 'igh', 't'], image: '💡' },
      { word: 'beach', sounds: ['b', 'ea', 'ch'], image: '🏖️' },
    ]
  };

  const generateWord = useCallback(() => {
    const words = wordLists[Math.min(level, 3)];
    const wordData = words[Math.floor(Math.random() * words.length)];
    
    // Generate wrong options
    const otherWords = words.filter(w => w.word !== wordData.word);
    const wrongOptions = otherWords
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map(w => w.word);
    
    const allOptions = [wordData.word, ...wrongOptions].sort(() => Math.random() - 0.5);
    
    setCurrentWord(wordData);
    setRevealedLetters([]);
    setOptions(allOptions);
    setBusPosition(0);
  }, [level]);

  const playSound = async (text, isLetter = false) => {
    if (isPlaying) return;
    setIsPlaying(true);
    
    try {
      const response = await api.post('/tts/generate', {
        text: isLetter ? text : text,
        voice: 'nova'
      });
      
      if (response.data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        audio.onended = () => setIsPlaying(false);
        audio.play();
      } else {
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsPlaying(false);
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.7;
      speechSynthesis.speak(utterance);
    }
  };

  const playBlendingSequence = async () => {
    if (!currentWord || isPlaying) return;
    
    // Reveal letters one by one with sounds
    for (let i = 0; i < currentWord.sounds.length; i++) {
      setRevealedLetters(prev => [...prev, i]);
      setBusPosition((i + 1) / currentWord.sounds.length * 100);
      await playSound(currentWord.sounds[i], true);
      await new Promise(resolve => setTimeout(resolve, 800));
    }
    
    // Play the blended word
    await new Promise(resolve => setTimeout(resolve, 500));
    await playSound(currentWord.word);
  };

  const startGame = useCallback(() => {
    setGameState('playing');
    setScore(0);
    setMistakes(0);
    setLevel(1);
    setWordsCompleted(0);
    setTimeLeft(60);
    generateWord();
  }, [generateWord]);

  // Timer effect
  useEffect(() => {
    let timer;
    if (gameState === 'playing' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            setGameState('finished');
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameState, timeLeft]);

  const handleAnswer = (selectedWord) => {
    if (selectedWord === currentWord.word) {
      // Correct!
      const points = 25 + (level * 10);
      setScore(s => s + points);
      setWordsCompleted(w => w + 1);
      
      toast.success(
        <div className="flex items-center gap-2">
          <span className="text-2xl">{currentWord.image}</span>
          <span>+{points} points! {currentWord.word.toUpperCase()}!</span>
        </div>
      );
      
      // Level up every 4 words
      if ((wordsCompleted + 1) % 4 === 0 && level < 3) {
        setLevel(l => l + 1);
        toast.success(`Level ${level + 1}! Longer words ahead! 🚌`);
      }
      
      generateWord();
    } else {
      // Wrong - count mistakes
      const newMistakes = mistakes + 1;
      setMistakes(newMistakes);
      
      if (newMistakes >= 3) {
        // End round after 3 mistakes
        toast.error(`Round over! The word was "${currentWord.word}".`);
        setGameState('finished');
      } else {
        toast.error(`Oops! Try again! (${3 - newMistakes} chances left)`);
      }
    }
  };

  // Save game on finish
  useEffect(() => {
    if (gameState === 'finished') {
      const saveGame = async () => {
        try {
          await api.post('/games/complete', {
            game_id: 'blending-bus',
            score: score,
            time_seconds: 0,
            metadata: { level, words_completed: wordsCompleted }
          });
        } catch (error) {
          console.error('Error saving game:', error);
        }
      };
      saveGame();
    }
  }, [gameState, score, level, wordsCompleted, api]);

  return (
    <StudentLayout>
      <div data-testid="blending-bus-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-yellow-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${timeLeft <= 10 ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                <span className="font-heading text-xl font-bold">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(3)].map((_, i) => (
                  <Heart key={i} className={`w-6 h-6 ${i < (3 - mistakes) ? 'text-red-500 fill-red-500' : 'text-slate-300'}`} />
                ))}
              </div>
              <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-600" />
                <span className="font-heading text-xl font-bold text-amber-700">{score}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Title */}
        <div className="student-card bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-300 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-yellow-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center text-3xl">
              🚌
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Blending Bus</h1>
              <p className="text-slate-600">
                {gameState === 'playing' ? `Level ${level} • Blend sounds to make words!` : 'Learn to blend sounds!'}
              </p>
            </div>
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-yellow-400 to-amber-500">
            <div className="text-8xl mb-6 animate-bounce">🚌</div>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Blending Bus</h2>
            <p className="text-lg text-yellow-100 mb-6">
              The bus picks up letter sounds and blends them into words!
            </p>
            <div className="text-yellow-100 mb-8 space-y-2">
              <p>🔊 Click PLAY to hear the sounds blend</p>
              <p>🔤 Watch: c - a - t = CAT!</p>
              <p>✅ Choose the correct word!</p>
            </div>
            <Button onClick={startGame} className="bg-white text-yellow-600 hover:bg-yellow-50 text-lg px-10 py-6">
              Hop On!
              <Bus className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentWord && (
          <div className="student-card bg-gradient-to-b from-sky-400 to-sky-500 min-h-[450px]">
            {/* Road with Bus */}
            <div className="relative h-24 bg-gray-700 rounded-xl mb-6 overflow-hidden">
              {/* Road markings */}
              <div className="absolute top-1/2 left-0 right-0 h-2 flex gap-4">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="flex-1 h-full bg-yellow-400" />
                ))}
              </div>
              
              {/* Bus */}
              <div
                className="absolute bottom-2 transition-all duration-500 text-6xl"
                style={{ left: `${busPosition}%`, transform: 'translateX(-50%)' }}
              >
                🚌
              </div>
              
              {/* Bus stops (letters) */}
              <div className="absolute top-2 left-0 right-0 flex justify-around">
                {currentWord.sounds.map((sound, i) => (
                  <div
                    key={i}
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold transition-all ${
                      revealedLetters.includes(i)
                        ? 'bg-green-400 text-white scale-110'
                        : 'bg-white/50 text-gray-600'
                    }`}
                  >
                    {revealedLetters.includes(i) ? sound : '?'}
                  </div>
                ))}
              </div>
            </div>

            {/* Word Display */}
            <div className="text-center mb-6">
              <div className="flex justify-center items-center gap-2 mb-4">
                <span className="text-6xl">{currentWord.image}</span>
              </div>
              
              {/* Letter boxes */}
              <div className="flex justify-center gap-2 mb-4">
                {currentWord.sounds.map((sound, i) => (
                  <div
                    key={i}
                    className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${
                      revealedLetters.includes(i)
                        ? 'bg-white text-sky-600 shadow-lg scale-105'
                        : 'bg-white/30 text-white/50'
                    }`}
                  >
                    {revealedLetters.includes(i) ? sound : '_'}
                  </div>
                ))}
              </div>
              
              {/* Play Button */}
              <Button
                onClick={playBlendingSequence}
                disabled={isPlaying}
                className={`bg-green-500 hover:bg-green-600 text-white px-8 py-4 text-lg ${isPlaying ? 'animate-pulse' : ''}`}
              >
                <Play className="w-5 h-5 mr-2" />
                {isPlaying ? 'Playing...' : 'Play Sounds'}
              </Button>
            </div>

            {/* Answer Options */}
            <div className="grid grid-cols-2 gap-3">
              {options.map((word) => (
                <button
                  key={word}
                  onClick={() => handleAnswer(word)}
                  className="bg-white rounded-xl p-4 shadow-lg hover:scale-105 transition-all active:scale-95"
                >
                  <span className="text-2xl font-heading font-bold text-sky-600">{word}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-yellow-400 to-amber-500">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce shadow-lg">
              <Trophy className="w-14 h-14 text-amber-500" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-2">Journey Complete!</h2>
            <p className="text-xl text-yellow-100 mb-4">
              You blended {wordsCompleted} words!
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-white fill-white" />
              <span className="font-heading text-4xl font-bold text-white">+{score}</span>
              <span className="text-yellow-100 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame} className="border-white text-white hover:bg-white/20">
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-white text-yellow-600 hover:bg-yellow-50">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default BlendingBusGame;
